/**
 * benvel-account-dropdown.js
 * Story 17.2 / Epic 17 — Account dropdown (header) signed-out + signed-in variants
 *
 * Vanilla ESM custom element — no Lit, no globals.
 * Handles: open/close, focus-trap (§D3 sentinel pattern), keyboard nav (AC4),
 *          mobile bottom-sheet swipe-to-dismiss (AC2 §D4), live wishlist counts (AC7).
 *
 * Decisions taken:
 *   §D1 Path A — this element owns the trigger + panel.
 *   §D3 — sentinel-based focus trap.
 *   §D4 — pointer events + 80px commit threshold.
 *   §D5 — sign-out via <form method="post"> (markup; JS does not override it).
 */

(function () {
  'use strict';

  /**
   * Read an array from localStorage safely (AC7 defensive pattern).
   * Returns 0 on parse error or missing key (private browsing).
   * @param {string} key
   * @returns {number}
   */
  function readWishlistCount(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (_) {
      return 0;
    }
  }

  /** Mobile breakpoint — matches Horizon @media (max-width: 749px) */
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  /** Minimum downward drag distance (px) to commit sheet close */
  const SWIPE_THRESHOLD = 80;

  class AccountDropdown extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._startY = 0;
      this._currentDeltaY = 0;

      // Bound handlers stored so they can be removed
      this._onDocClick    = this._onDocClick.bind(this);
      this._onDocKeydown  = this._onDocKeydown.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp   = this._onPointerUp.bind(this);
      this._onPointerCancel = this._onPointerCancel.bind(this);
      this._onResize      = this._onResize.bind(this);
    }

    connectedCallback() {
      this._trigger   = this.querySelector('[data-testid="account-dropdown-trigger"]');
      this._panel     = this.querySelector('#account-dropdown-panel');
      this._scrim     = this.querySelector('.account-dropdown__scrim');
      this._grabber   = this.querySelector('.account-dropdown__grabber');
      this._closeBtns = this.querySelectorAll('[data-close-trigger]');
      this._sentinelStart = this.querySelector('[data-focus-sentinel="start"]');
      this._sentinelEnd   = this.querySelector('[data-focus-sentinel="end"]');

      // Locale templates for wishlist sublabels (F4/F5 — route through t: filter for EN parity)
      this._localeWishlistSublabel = this.getAttribute('data-locale-wishlist-sublabel') || '';
      this._localeLooksSublabel    = this.getAttribute('data-locale-looks-sublabel') || '';

      if (!this._trigger || !this._panel) return;

      // Trigger: click, Enter, Space
      this._trigger.addEventListener('click', () => this._isOpen ? this.close() : this.open());
      this._trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._isOpen ? this.close() : this.open();
        }
      });

      // Close buttons (mobile "Cerrar" button)
      this._closeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.close({ restoreFocus: false }));
      });

      // Sentinel focus-trap (§D3)
      if (this._sentinelStart) {
        this._sentinelStart.addEventListener('focus', () => {
          this._moveFocusToLast();
        });
      }
      if (this._sentinelEnd) {
        this._sentinelEnd.addEventListener('focus', () => {
          this._moveFocusToFirst();
        });
      }

      // Scrim click — close without focus restore
      if (this._scrim) {
        this._scrim.addEventListener('click', () => this.close({ restoreFocus: false }));
      }

      // Resize — close panel if viewport crosses mobile breakpoint
      window.addEventListener('resize', this._onResize, { passive: true });

      // Populate live wishlist counts on connect (AC7)
      this._updateWishlistCounts();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._unbindDocListeners();
    }

    /**
     * Open the dropdown/bottom-sheet.
     * @param {Object} [opts]
     * @param {boolean} [opts.focusFirst=true] - Move focus to first interactive element.
     */
    open(opts = {}) {
      const { focusFirst = true } = opts;
      this._isOpen = true;
      this._panel.setAttribute('data-state', 'open');
      this._panel.setAttribute('aria-hidden', 'false');
      this._trigger.setAttribute('aria-expanded', 'true');

      if (MOBILE_MQ.matches) {
        this._showScrim();
        this._bindSwipeListeners();
      }

      // Refresh wishlist counts each time panel opens (AC7)
      this._updateWishlistCounts();

      // Bind global listeners for ESC + outside-click
      document.addEventListener('click', this._onDocClick, true);
      document.addEventListener('keydown', this._onDocKeydown);

      if (focusFirst) {
        // Defer 1 frame so panel is visible before focus move
        requestAnimationFrame(() => this._moveFocusToFirst());
      }
    }

    /**
     * Close the dropdown/bottom-sheet.
     * @param {Object} [opts]
     * @param {boolean} [opts.restoreFocus=true] - Restore focus to trigger (ESC path).
     */
    close(opts = {}) {
      const { restoreFocus = true } = typeof opts === 'boolean'
        ? { restoreFocus: opts }
        : opts;

      this._isOpen = false;
      this._panel.setAttribute('data-state', 'closed');
      this._panel.setAttribute('aria-hidden', 'true');
      this._trigger.setAttribute('aria-expanded', 'false');

      // Remove inline transform that swipe may have set
      this._panel.style.transform = '';
      this._panel.removeAttribute('data-dragging');

      this._hideScrim();
      this._unbindSwipeListeners();
      this._unbindDocListeners();

      if (restoreFocus) {
        this._trigger.focus();
      }
    }

    // ── Focus helpers ──────────────────────────────────────────────────────

    _getFocusableElements() {
      const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
      ].join(', ');
      return Array.from(this._panel.querySelectorAll(focusableSelector)).filter(
        el => !el.matches('[data-focus-sentinel]') && el.offsetParent !== null
      );
    }

    _moveFocusToFirst() {
      const items = this._getFocusableElements();
      if (items.length) items[0].focus();
    }

    _moveFocusToLast() {
      const items = this._getFocusableElements();
      if (items.length) items[items.length - 1].focus();
    }

    // ── Document-level listeners (active while panel is open) ──────────────

    _unbindDocListeners() {
      document.removeEventListener('click', this._onDocClick, true);
      document.removeEventListener('keydown', this._onDocKeydown);
    }

    _onDocClick(e) {
      // Close if click is outside both the trigger and the panel
      if (!this.contains(e.target)) {
        this.close({ restoreFocus: false });
      }
    }

    _onDocKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close({ restoreFocus: true });
      }
    }

    // ── Resize handling ────────────────────────────────────────────────────

    _onResize() {
      if (this._isOpen) {
        // Close on viewport-crossing to avoid mismatched state
        this.close({ restoreFocus: false });
      }
    }

    // ── Mobile scrim ───────────────────────────────────────────────────────

    _showScrim() {
      if (!this._scrim) return;
      this._scrim.setAttribute('data-scrim-state', 'open');
      this._scrim.removeAttribute('aria-hidden');
    }

    _hideScrim() {
      if (!this._scrim) return;
      this._scrim.setAttribute('data-scrim-state', 'closed');
      this._scrim.setAttribute('aria-hidden', 'true');
    }

    // ── Swipe-to-dismiss (mobile, §D4) ────────────────────────────────────

    _bindSwipeListeners() {
      const target = this._grabber || this._panel;
      target.addEventListener('pointerdown', this._onPointerDown);
    }

    _unbindSwipeListeners() {
      const target = this._grabber || this._panel;
      target.removeEventListener('pointerdown', this._onPointerDown);
      // Also ensure pointermove/up/cancel are cleaned up on the panel
      this._panel.removeEventListener('pointermove', this._onPointerMove);
      this._panel.removeEventListener('pointerup', this._onPointerUp);
      this._panel.removeEventListener('pointercancel', this._onPointerCancel);
    }

    _onPointerDown(e) {
      // Only allow drag from top 56px of panel or the grabber itself
      const panelRect = this._panel.getBoundingClientRect();
      const grabberZone = e.clientY < panelRect.top + 56;
      if (!grabberZone && e.target !== this._grabber) return;

      this._startY = e.clientY;
      this._currentDeltaY = 0;
      this._panel.setPointerCapture(e.pointerId);
      this._panel.setAttribute('data-dragging', 'true');

      this._panel.addEventListener('pointermove', this._onPointerMove);
      this._panel.addEventListener('pointerup', this._onPointerUp);
      this._panel.addEventListener('pointercancel', this._onPointerCancel);
    }

    _onPointerMove(e) {
      const deltaY = e.clientY - this._startY;
      if (deltaY <= 0) return; // no upward drag
      this._currentDeltaY = deltaY;
      this._panel.style.transform = `translateY(${deltaY}px)`;
    }

    _onPointerUp() {
      this._panel.removeAttribute('data-dragging');
      this._panel.removeEventListener('pointermove', this._onPointerMove);
      this._panel.removeEventListener('pointerup', this._onPointerUp);
      this._panel.removeEventListener('pointercancel', this._onPointerCancel);

      if (this._currentDeltaY > SWIPE_THRESHOLD) {
        this.close({ restoreFocus: false });
      } else {
        // Snap back
        this._panel.style.transform = '';
      }
      this._currentDeltaY = 0;
    }

    _onPointerCancel() {
      this._panel.removeAttribute('data-dragging');
      this._panel.style.transform = '';
      this._currentDeltaY = 0;
      this._panel.removeEventListener('pointermove', this._onPointerMove);
      this._panel.removeEventListener('pointerup', this._onPointerUp);
      this._panel.removeEventListener('pointercancel', this._onPointerCancel);
    }

    // ── Live wishlist counts (AC7) ─────────────────────────────────────────

    _updateWishlistCounts() {
      const productsCount = readWishlistCount('benvel.wishlist.products');
      const looksCount    = readWishlistCount('benvel.wishlist.looks');

      // Signed-in product count target
      // Uses locale template from data-locale-wishlist-sublabel (Liquid t: filter handles pluralization + locale)
      this.querySelectorAll('[data-wishlist-count-target="products"]').forEach(el => {
        if (productsCount > 0 && this._localeWishlistSublabel) {
          el.textContent = this._localeWishlistSublabel.replace('__COUNT__', String(productsCount));
        } else {
          el.textContent = '';
        }
      });

      // Signed-in looks count target
      this.querySelectorAll('[data-wishlist-count-target="looks"]').forEach(el => {
        if (looksCount > 0 && this._localeLooksSublabel) {
          el.textContent = this._localeLooksSublabel.replace('__COUNT__', String(looksCount));
        } else {
          el.textContent = '';
        }
      });
    }
  }

  if (!customElements.get('account-dropdown')) {
    customElements.define('account-dropdown', AccountDropdown);
  }
})();
